<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { formatEur } from '@/composables/useSubscription'

/**
 * The cross-sell add-on screen (shared by health / health-top-up / AURA): a list
 * of add-on cards with their features, plus a decline link. Presentational — the
 * parent resolves the add-ons + optional lead text and handles navigation via
 * add/decline.
 */

export type CrossSellCard = {
  /** Add-on value used for selection + as the v-for key. */
  key: string
  name: string
  /** Yearly price in euros (shown as a "+…/year" delta). */
  priceYear: number
  features: string[]
}

defineProps<{
  /** Two-part "offer + saving" notice (health / health-top-up), or null. */
  notice?: { lead: string; strong: string } | null
  addons: CrossSellCard[]
}>()

defineEmits<{ add: [key: string]; decline: [] }>()

const { t, locale } = useI18n()

function formatPrice(value: number): string {
  return formatEur(value, locale.value)
}
</script>

<template>
  <div class="journey-crosssell">
    <h1 id="journey-question" class="journey-crosssell__title">
      {{ t('subscription.crossSell.title') }}
    </h1>
    <p v-if="notice" class="journey-form__notice">
      {{ notice.lead }}<strong>{{ notice.strong }}</strong>
    </p>
    <div class="journey-crosssell__grid">
      <article v-for="addon in addons" :key="addon.key" class="crosssell-card">
        <div class="crosssell-card__head">
          <h2 class="crosssell-card__name">{{ addon.name }}</h2>
          <span class="crosssell-card__price">
            +{{ formatPrice(addon.priceYear) }}{{ t('subscription.results.perYear') }}
          </span>
        </div>
        <ul class="crosssell-card__features">
          <li
            v-for="(feature, i) in addon.features"
            :key="i"
            class="crosssell-card__feature"
          >
            <svg class="crosssell-card__bullet" viewBox="0 0 20 20" width="14" height="14" aria-hidden="true">
              <path d="M4 10l4 4 8-9" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>{{ feature }}</span>
          </li>
        </ul>
        <button type="button" class="crosssell-card__add" @click="$emit('add', addon.key)">
          {{ t('subscription.crossSell.add') }}
        </button>
      </article>
    </div>
    <button type="button" class="crosssell-decline" @click="$emit('decline')">
      {{ t('subscription.crossSell.decline') }}
    </button>
  </div>
</template>

<style scoped>
.journey-crosssell {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}
.journey-crosssell__title {
  font-size: var(--text-xl);
  line-height: var(--leading-tight);
}
.journey-crosssell__grid {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: 1fr;
  align-items: stretch;
}
@media (min-width: 40rem) {
  .journey-crosssell__grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

.crosssell-card {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.25rem;
  background: var(--color-white);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-lg);
  transition:
    transform var(--dur-base) var(--ease-spring),
    box-shadow var(--dur-base) var(--ease-out),
    border-color var(--dur-base) var(--ease-out);
}
.crosssell-card:hover {
  transform: translateY(-3px);
  border-color: var(--primary);
  box-shadow: var(--shadow-md);
}
.crosssell-card__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}
.crosssell-card__name {
  font-size: var(--text-lg);
  font-weight: 800;
  color: var(--color-chocolate);
}
.crosssell-card__price {
  font-size: var(--text-base);
  font-weight: 800;
  color: var(--primary);
  white-space: nowrap;
}
.crosssell-card__features {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  margin: 0;
  padding: 0;
  list-style: none;
}
.crosssell-card__feature {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: var(--text-sm);
  line-height: 1.4;
  color: var(--color-chocolate);
}
.crosssell-card__bullet {
  flex-shrink: 0;
  margin-top: 0.15rem;
  color: var(--primary);
}
.crosssell-card__add {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: auto;
  padding: 0.6rem 1rem;
  background: var(--primary);
  border: 1px solid var(--primary);
  border-radius: var(--radius-md);
  font-size: var(--text-sm);
  font-weight: 700;
  color: var(--text-on-primary);
  cursor: pointer;
  transition:
    background var(--dur-fast) var(--ease-out),
    border-color var(--dur-fast) var(--ease-out),
    transform var(--dur-base) var(--ease-spring);
}
.crosssell-card__add:hover {
  background: var(--primary-hover);
  border-color: var(--primary-hover);
  transform: translateY(-1px);
}
.crosssell-card__add:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
}

.crosssell-decline {
  align-self: center;
  padding: 0.6rem 1.5rem;
  background: var(--color-white);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  font-size: var(--text-sm);
  font-weight: 700;
  color: var(--color-chocolate);
  cursor: pointer;
  transition:
    border-color var(--dur-base) var(--ease-out),
    background var(--dur-base) var(--ease-out),
    transform var(--dur-base) var(--ease-spring);
}
.crosssell-decline:hover {
  border-color: var(--primary);
  background: color-mix(in oklab, var(--color-peach) 18%, white);
  transform: translateY(-2px);
}
.crosssell-decline:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
}

/* ---- Écran récapitulatif + formulaire ---- */

.journey-form__notice {
  font-size: var(--text-xs);
  line-height: 1.5;
  color: var(--text-muted);
  padding: 0.75rem 0.9rem;
  background: var(--bg-soft);
  border-radius: var(--radius-md);
}
</style>
